import React, { useCallback } from 'react';
import { Card, IconButton, Stack, Tooltip, Typography } from '@mui/material';

import { DeleteIcon } from '~assets/icons';
import { ConfirmationDialog } from '~components/atoms';
import { useModal } from '~hooks/useModal';
import { StationFilterOption } from '~types';

type Props = {
  index: number;
  stationImageUrl: string;
  placementName: string;
  station: StationFilterOption | null;
  onClick: (_index: number) => void;
  onRemovePlacement: (_: number) => void;
  /**
   * Only a swap or a relocation carries these. A drop station placement has neither,
   * and the card then renders exactly as it did before.
   */
  unpinningReason?: string;
  unpinningNote?: string;
};

const LabelledDetail: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Typography
    variant="body2"
    color="text.placeholder"
    sx={{ maxWidth: '100%', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
  >
    <Typography
      component="span"
      variant="body2"
      color="text.black"
      fontWeight={600}
      mr={0.5}
    >
      {label}:
    </Typography>
    {value}
  </Typography>
);

export const PlacementReviewCard: React.FC<Props> = ({
  index,
  stationImageUrl,
  placementName,
  station,
  onClick,
  onRemovePlacement,
  unpinningReason,
  unpinningNote,
}) => {
  const { isOpen, openModal, closeModal } = useModal();
  const handleDeletePlacement = useCallback(
    (index: number) => {
      onRemovePlacement(index);
      closeModal();
    },
    [onRemovePlacement, closeModal],
  );

  return (
    <>
      <Stack
        key={index}
        gap={1.5}
        style={{
          minHeight: 72,
          width: '100%',
          border: '1px solid #D1D5DB',
          borderRadius: '8px',
          cursor: 'pointer',
        }}
        direction="row"
      >
        <Card
          style={{
            width: 72,
            backgroundColor: '#E5E7EB',
            borderRadius: '6.5px 0 0 6.5px',
            display: 'flex', // Enable flexbox
            justifyContent: 'center', // Center horizontally
            alignItems: 'center',
          }}
        >
          <img
            src={stationImageUrl}
            alt="cf-logo"
            onClick={() => onClick(index)}
          />
        </Card>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <Stack
            gap={0.5}
            py={1}
            alignItems="flex-start"
            justifyContent="center"
            onClick={() => onClick(index)}
            sx={{ flexGrow: 1, width: 0 }}
          >
            <Tooltip title={placementName}>
              <Typography
                variant="subtitle1"
                color="text.black"
                fontWeight={900}
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%',
                }}
              >
                {placementName}
              </Typography>
            </Tooltip>
            <Tooltip title={`${station?.type} · IMEI: ${station?.imei}`}>
              <Typography
                variant="body1"
                color="text.placeholder"
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%',
                }}
              >
                {`${station?.type} · IMEI: ${station?.imei}`}
              </Typography>
            </Tooltip>
            {unpinningReason && (
              <LabelledDetail label="Reason" value={unpinningReason} />
            )}
            {unpinningNote && (
              <LabelledDetail label="Note" value={unpinningNote} />
            )}
          </Stack>
          <IconButton
            sx={{
              width: '40px',
              height: '40px',
              p: 0.75,
              ':hover': {
                borderRadius: '100px',
              },
            }}
            onClick={openModal}
          >
            <DeleteIcon />
          </IconButton>
        </Stack>
      </Stack>
      <ConfirmationDialog
        open={isOpen}
        title="Delete Placement?"
        content="Are you sure you want to remove this Placement? All Placement data will be deleted."
        btnDeleteText="Delete Placement"
        onClose={closeModal}
        onDelete={() => handleDeletePlacement(index)}
        onCancel={closeModal}
      />
    </>
  );
};
